import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Plus, Eye, Pencil, Trash2, Send, ArrowLeft, X, Mail } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { isStaffOrAbove } from '../lib/tier';
import { RenderMarkdownArticle } from '../lib/markdown';
import SpellCheckDialog from '../components/SpellCheckDialog';
import { useSpellCheckOnSave } from '../hooks/useSpellCheckOnSave';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch (_e) {
    return '—';
  }
};

const blankDraft = { title: '', body: '', excerpt: '', meta_description: '', cover_image_url: '', tags: [] };

const AdminBlog = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // null | {slug?: string, ...payload}
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    if (user === undefined) return;
    if (!user || !isStaffOrAbove(user)) {
      navigate('/home', { replace: true });
    }
  }, [user, navigate]);

  const reload = async () => {
    try {
      const r = await axios.get(`${API}/admin/blog/posts`, { withCredentials: true });
      setPosts(r.data?.posts || []);
    } catch (_err) {
      toast.error('Could not load posts');
    }
  };

  useEffect(() => { reload(); }, []);

  const startNew = () => {
    setEditing({ ...blankDraft });
    setPreviewing(false);
  };

  const startEdit = async (slug) => {
    try {
      const r = await axios.get(`${API}/admin/blog/posts/${slug}`, { withCredentials: true });
      setEditing({ ...r.data, original_slug: slug });
      setPreviewing(false);
    } catch (_err) {
      toast.error('Could not open post');
    }
  };

  const save = async () => {
    if (!editing?.title?.trim() || !editing?.body?.trim()) {
      toast.error('Title and body are required');
      return;
    }
    sc.save();
  };

  const buildPayload = useCallback(() => ({
    title: editing?.title || '',
    body: editing?.body || '',
    excerpt: editing?.excerpt || null,
    meta_description: editing?.meta_description || null,
    cover_image_url: editing?.cover_image_url || null,
    tags: editing?.tags || [],
  }), [editing]);

  const persist = useCallback(async (payload) => {
    setBusy(true);
    try {
      if (editing?.original_slug) {
        await axios.put(`${API}/admin/blog/posts/${editing.original_slug}`, payload, { withCredentials: true });
        toast.success('Post updated');
      } else {
        await axios.post(`${API}/admin/blog/posts`, payload, { withCredentials: true });
        toast.success('Draft created');
      }
      setEditing(null);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  }, [editing]);

  // Run spell-check on the title + body + meta description before persisting.
  // The hook surfaces the SpellCheckDialog automatically when issues are found;
  // user can apply suggestions, save-as-is, or cancel. Mirrors the pattern in
  // AdminReleases.js + 4 other CMS editors.
  const sc = useSpellCheckOnSave({ buildPayload, persist });

  const togglePublish = async (slug, makePublished) => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/blog/posts/${slug}/publish`,
        { publish: makePublished },
        { withCredentials: true },
      );
      toast.success(makePublished ? 'Published' : 'Reverted to draft');
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Publish toggle failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Permanently delete "${slug}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/blog/posts/${slug}`, { withCredentials: true });
      toast.success('Deleted');
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  // Email every (non-unsubscribed) waitlist subscriber about a published post.
  // Idempotent on the server — re-running with the same slug returns 409
  // unless we explicitly pass force=true (we ask the user to confirm twice
  // before letting them resend).
  const broadcast = async (slug, alreadySent) => {
    const firstPrompt = alreadySent
      ? `This post has already been broadcast. Resend to the whole waitlist?`
      : `Send "${slug}" to every waitlist subscriber? This cannot be undone.`;
    if (!window.confirm(firstPrompt)) return;
    setBusy(true);
    try {
      const url = `${API}/admin/blog/posts/${slug}/broadcast${alreadySent ? '?force=true' : ''}`;
      const r = await axios.post(url, {}, { withCredentials: true });
      const { sent = 0, failed = 0, subscribers_total = 0 } = r.data || {};
      toast.success(`Broadcast sent — ${sent}/${subscribers_total} delivered${failed ? `, ${failed} failed` : ''}`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Broadcast failed');
    } finally {
      setBusy(false);
    }
  };

  const editorBody = useMemo(() => editing?.body || '', [editing?.body]);

  // -------- editor view --------------------------------------------------
  if (editing) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] p-6 sm:p-10" data-testid="admin-blog-editor">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => setEditing(null)}
              className="inline-flex items-center gap-2 text-sm text-[#475569] hover:text-[#020617]"
              data-testid="admin-blog-editor-back"
            >
              <ArrowLeft className="w-4 h-4" /> Back to list
            </button>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setPreviewing((p) => !p)}
                className="inline-flex items-center gap-2 text-sm font-medium px-3 py-1.5 rounded-lg border border-[#E2E8F0] bg-white hover:border-[#CBD5E1]"
                data-testid="admin-blog-toggle-preview"
              >
                <Eye className="w-4 h-4" /> {previewing ? 'Edit' : 'Preview'}
              </button>
              <button
                onClick={save}
                disabled={busy}
                className="inline-flex items-center gap-2 text-sm font-medium px-4 py-1.5 rounded-lg bg-[#020617] text-white disabled:opacity-50"
                data-testid="admin-blog-save"
              >
                {editing.original_slug ? 'Save changes' : 'Create draft'}
              </button>
            </div>
          </div>

          {previewing ? (
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 sm:p-10">
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4 text-[#020617]">{editing.title || 'Untitled'}</h1>
              <p className="text-xs text-[#64748B] mb-8">Preview — not saved yet</p>
              <RenderMarkdownArticle text={editorBody} />
            </div>
          ) : (
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-6 sm:p-8 space-y-5">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#64748B]">Title</label>
                <input
                  type="text"
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  placeholder="How to budget on UK 2026 minimum wage"
                  className="w-full mt-1 text-lg font-bold px-3 py-2 border border-[#E2E8F0] rounded-lg focus:outline-none focus:border-[#0052FF]"
                  data-testid="admin-blog-title-input"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#64748B]">Cover image URL (optional)</label>
                <input
                  type="url"
                  value={editing.cover_image_url || ''}
                  onChange={(e) => setEditing({ ...editing, cover_image_url: e.target.value })}
                  placeholder="https://…"
                  className="w-full mt-1 text-sm px-3 py-2 border border-[#E2E8F0] rounded-lg focus:outline-none focus:border-[#0052FF]"
                  data-testid="admin-blog-cover-input"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#64748B]">Meta description (SEO, 160 chars)</label>
                <input
                  type="text"
                  value={editing.meta_description || ''}
                  onChange={(e) => setEditing({ ...editing, meta_description: e.target.value })}
                  maxLength={320}
                  placeholder="Auto-derived from body if left blank"
                  className="w-full mt-1 text-sm px-3 py-2 border border-[#E2E8F0] rounded-lg focus:outline-none focus:border-[#0052FF]"
                  data-testid="admin-blog-meta-input"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#64748B]">Body (Markdown — supports ## headings, **bold**, *italic*, lists, [links](https://…), &gt; quotes, ```code```)</label>
                <textarea
                  value={editing.body}
                  onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                  rows={20}
                  className="w-full mt-1 text-sm font-mono px-3 py-2 border border-[#E2E8F0] rounded-lg focus:outline-none focus:border-[#0052FF] leading-relaxed"
                  placeholder="## Section heading&#10;&#10;Paragraph content here. **Bold** and *italic* work, as do [links](https://example.com).&#10;&#10;- Bullet one&#10;- Bullet two&#10;&#10;> A quote block.&#10;&#10;```&#10;Code block&#10;```"
                  data-testid="admin-blog-body-input"
                />
              </div>
            </div>
          )}
        </div>
        <SpellCheckDialog {...sc.dialogProps} />
      </div>
    );
  }

  // -------- list view ----------------------------------------------------
  return (
    <div className="min-h-screen bg-[#F8FAFC] p-6 sm:p-10" data-testid="admin-blog-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#020617]">Blog posts</h1>
            <p className="text-sm text-[#64748B] mt-1">Drafts &amp; published — the public sees only published.</p>
          </div>
          <button
            onClick={startNew}
            className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg bg-[#020617] text-white hover:bg-[#0f172a]"
            data-testid="admin-blog-new"
          >
            <Plus className="w-4 h-4" /> New post
          </button>
        </div>

        {posts.length === 0 ? (
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-12 text-center" data-testid="admin-blog-empty">
            <p className="text-[#475569] mb-2">No posts yet.</p>
            <p className="text-xs text-[#64748B]">Click &ldquo;New post&rdquo; to write the first one.</p>
          </div>
        ) : (
          <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F1F5F9] text-left">
                <tr>
                  <th className="px-4 py-3 font-bold text-[#475569]">Title</th>
                  <th className="px-4 py-3 font-bold text-[#475569]">Status</th>
                  <th className="px-4 py-3 font-bold text-[#475569]">Updated</th>
                  <th className="px-4 py-3 font-bold text-[#475569] w-1"></th>
                </tr>
              </thead>
              <tbody>
                {posts.map((p) => (
                  <tr key={p.slug} className="border-t border-[#F1F5F9]" data-testid={`admin-blog-row-${p.slug}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#020617]">{p.title}</div>
                      <div className="text-xs text-[#64748B] mt-0.5">/{p.slug}</div>
                    </td>
                    <td className="px-4 py-3">
                      {p.status === 'published' ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700">Published</span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider bg-amber-50 text-amber-700">Draft</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[#64748B]">{formatDate(p.updated_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => startEdit(p.slug)}
                          title="Edit"
                          className="p-1.5 rounded hover:bg-[#F1F5F9] text-[#475569]"
                          data-testid={`admin-blog-edit-${p.slug}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => togglePublish(p.slug, p.status !== 'published')}
                          title={p.status === 'published' ? 'Unpublish' : 'Publish'}
                          disabled={busy}
                          className="p-1.5 rounded hover:bg-[#F1F5F9] text-[#475569]"
                          data-testid={`admin-blog-publish-${p.slug}`}
                        >
                          {p.status === 'published' ? <X className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                        </button>
                        {p.status === 'published' && (
                          <button
                            onClick={() => broadcast(p.slug, !!p.broadcast_sent_at)}
                            title={p.broadcast_sent_at ? `Already broadcast — click to resend` : 'Email the waitlist about this post'}
                            disabled={busy}
                            className={`p-1.5 rounded hover:bg-[#EFF6FF] ${p.broadcast_sent_at ? 'text-[#94A3B8]' : 'text-[#0052FF]'}`}
                            data-testid={`admin-blog-broadcast-${p.slug}`}
                          >
                            <Mail className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => remove(p.slug)}
                          title="Delete"
                          disabled={busy}
                          className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
                          data-testid={`admin-blog-delete-${p.slug}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBlog;
